import { Badge } from '@/components/ui/badge';
import { m } from 'motion/react';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import {
  fadeInUp,
  staggerContainer,
  staggerChild,
  defaultViewport,
  getReducedMotionVariants,
} from '@/lib/motion';
import { useTranslation } from '@/i18n/i18n';

export const SupportedForms = () => {
  const shouldReduceMotion = useReducedMotion();
  const { t } = useTranslation();

  const formCategories = [
    {
      category: t('supportedForms.cat1'),
      forms: [
        { name: 'JPK_V7M', description: t('supportedForms.jpkV7m') },
        { name: 'JPK_PKPiR', description: t('supportedForms.jpkPkpir') },
        { name: 'JPK_EWP', description: t('supportedForms.jpkEwp') },
        { name: 'JPK_ST', description: t('supportedForms.jpkSt') },
      ],
    },
    {
      category: t('supportedForms.cat2'),
      forms: [
        { name: 'PIT-28', description: t('supportedForms.pit28') },
        { name: 'PIT-36', description: t('supportedForms.pit36') },
        { name: 'PIT-36L', description: t('supportedForms.pit36l') },
        { name: 'PIT/B', description: t('supportedForms.pitB') },
      ],
    },
    {
      category: t('supportedForms.cat3'),
      forms: [
        { name: 'ZUS DRA', description: t('supportedForms.zusDra') },
        { name: 'ZUS RCA', description: t('supportedForms.zusRca') },
        { name: 'ZUS RZA', description: t('supportedForms.zusRza') },
        { name: 'ZUS RSA', description: t('supportedForms.zusRsa') },
      ],
    },
    {
      category: t('supportedForms.cat4'),
      forms: [
        { name: 'VAT-UE', description: t('supportedForms.vatUe') },
        { name: 'VAT-UEK', description: t('supportedForms.vatUek') },
        { name: 'VAT-8', description: t('supportedForms.vat8') },
        { name: 'VAT-9M', description: t('supportedForms.vat9m') },
      ],
    },
  ];

  return (
    <section aria-labelledby="supported-forms-heading" className="py-16 px-6 md:px-8 bg-card">
      <div className="max-w-6xl mx-auto">
        <m.div
          className="text-center mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
          variants={shouldReduceMotion ? getReducedMotionVariants(fadeInUp) : fadeInUp}
        >
          <Badge className="mb-4" variant="outline">
            {t('supportedForms.badge')}
          </Badge>
          <h2
            id="supported-forms-heading"
            className="text-3xl md:text-4xl lg:text-5xl font-semibold mb-4 tracking-tight"
          >
            {t('supportedForms.heading')
              .split('<primary>')
              .map((part, i) => {
                if (i === 0) return part;
                const [primary, rest] = part.split('</primary>');
                return (
                  <span key={primary}>
                    <span className="text-primary">{primary}</span>
                    {rest}
                  </span>
                );
              })}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('supportedForms.subheading')}
          </p>
        </m.div>

        <m.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
          variants={
            shouldReduceMotion ? getReducedMotionVariants(staggerContainer) : staggerContainer
          }
        >
          {formCategories.map((category) => (
            <m.div
              key={category.category}
              className="space-y-3"
              variants={shouldReduceMotion ? getReducedMotionVariants(staggerChild) : staggerChild}
            >
              <h3 className="font-mono text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                {category.category}
              </h3>
              <div className="space-y-2">
                {category.forms.map((form) => (
                  <div
                    key={form.name}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg bg-background border border-border/50 hover:border-primary/20 transition-colors"
                  >
                    <span className="text-sm font-bold text-foreground whitespace-nowrap">
                      {form.name}
                    </span>
                    <span className="text-xs text-muted-foreground">{form.description}</span>
                  </div>
                ))}
              </div>
            </m.div>
          ))}
        </m.div>
      </div>
    </section>
  );
};
